import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, User, LogOut } from "lucide-react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from './Auth/firebase';
import logo from './logo.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState(null);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Listen for authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setShowProfileDropdown(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Base navigation items (always visible)
  const baseNavItems = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  // Authenticated user navigation items
  const authNavItems = [
    { name: "Passbook", path: "/schemes" },
  ];

  // Combine nav items based on authentication status
  const navItems = user ? [...baseNavItems.slice(0, 2), ...authNavItems, ...baseNavItems.slice(2)] : baseNavItems;

  return (
    <>
      <header className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled 
          ? "bg-[#041a0f]/95 backdrop-blur-md shadow-lg border-b border-[#eddca6]/20 py-3" 
          : "bg-transparent border-transparent py-5"
      }`}>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            
            {/* Logo/Brand */}
            <Link to="/" className="flex-shrink-0 flex items-center group">
              <img 
                src={logo}
                alt="Chirayil Gold and Diamonds Logo"
                className="h-11 w-11 mr-3 transition-all duration-500 group-hover:rotate-[360deg] brightness-0 invert"
              />
              <span className="flex flex-col">
                <span className="text-lg font-serif font-bold tracking-widest transition-all duration-300 text-[#eddca6] hover:text-[#dfc27d]">
                  CHIRAYIL
                </span>
                <span className="text-[9px] uppercase tracking-[0.25em] font-medium -mt-1 transition-colors duration-300 text-white/80 group-hover:text-[#eddca6]">
                  Gold & Diamonds
                </span>
                <span className="text-[8px] sm:text-[9px] uppercase tracking-wider font-semibold text-[#eddca6]/90 mt-0.5 font-sans">
                  A unit from Vaikom Manufacturer • Wholesale and Retail
                </span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <div className="flex space-x-2">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`relative px-4 py-2 text-xs font-semibold tracking-widest uppercase transition-all duration-300 rounded-lg ${
                      location.pathname === item.path 
                        ? "text-[#0b3a24] bg-[#eddca6] shadow-sm"
                        : "text-white hover:text-[#eddca6] hover:bg-white/10"
                    }`}
                  >
                    {item.name}
                    {location.pathname === item.path && !scrolled && (
                      <span className="absolute inset-x-4 bottom-1 h-0.5 bg-[#eddca6] rounded-full"></span>
                    )}
                  </Link>
                ))}
              </div>
              
              {/* Auth Button */}
              <div className="ml-4 pl-6 border-l border-gold-200/20">
                {user ? (
                  <div className="relative">
                    <button
                      onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                      className="flex items-center gap-2 px-5 py-2 rounded-lg shadow-md transition-all duration-300 hover:scale-[1.02] text-xs font-semibold uppercase tracking-wider bg-[#eddca6] text-[#0b3a24] hover:bg-[#dfc27d]"
                    >
                      <User className="h-3.5 w-3.5" />
                      <span>Profile</span>
                    </button>
                    
                    {showProfileDropdown && (
                      <div className="absolute right-0 mt-3 w-52 bg-white rounded-xl shadow-xl border border-gold-100/50 z-50 overflow-hidden">
                        <div className="p-4 bg-gradient-to-b from-[#faf6ee] to-transparent border-b border-gray-100">
                          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Signed in as</p>
                          <p className="text-xs font-bold text-luxury-charcoal truncate mt-0.5">{user.phoneNumber}</p>
                        </div>
                        <div className="py-1">
                          <Link
                            to="/profile"
                            className="flex items-center px-4 py-2.5 text-xs font-medium text-gray-700 hover:bg-[#faf6ee] transition-colors"
                            onClick={() => setShowProfileDropdown(false)}
                          >
                            <User className="h-3.5 w-3.5 mr-2 text-[#0b3a24]" />
                            View Profile
                          </Link>
                          <button
                            onClick={handleLogout}
                            className="flex items-center w-full px-4 py-2.5 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors text-left"
                          >
                            <LogOut className="h-3.5 w-3.5 mr-2" />
                            Sign Out
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link 
                    to="/login"
                    className="flex cursor-pointer items-center gap-2 px-6 py-2 rounded-lg shadow-md transition-all duration-300 hover:scale-[1.02] text-xs font-semibold uppercase tracking-wider bg-[#eddca6] text-[#0b3a24] hover:bg-[#dfc27d]"
                  >
                    <span>Login</span>
                  </Link>
                )}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsOpen(true)}
                className="inline-flex items-center justify-center p-2 rounded-lg focus:outline-none transition-colors duration-300 text-white hover:text-[#eddca6] hover:bg-white/10"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="fixed inset-0 z-[9999]">
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-black/40 backdrop-blur-md transition-opacity duration-300" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Mobile Menu Panel */}
          <div className="absolute inset-y-0 right-0 w-72 bg-[#fdfbf9] shadow-2xl border-l border-gold-100/50 transform transition-transform duration-300 ease-in-out">
            <div className="h-full flex flex-col">
              
              {/* Header with close button */}
              <div className="flex items-center justify-between p-6 border-b border-gold-100/20">
                <div className="flex items-center">
                  <img 
                    src={logo}
                    alt="Logo"
                    className="h-8 w-8 mr-2"
                  />
                  <span className="text-md font-serif font-bold tracking-widest text-[#0b3a24]">CHIRAYIL</span>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 rounded-full text-gray-500 hover:text-[#0b3a24] hover:bg-[#faf6ee] transition-colors duration-300"
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {/* Navigation items */}
              <nav className="flex-1 overflow-y-auto py-4 px-6">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`block px-4 py-3 my-1.5 text-xs font-semibold uppercase tracking-widest rounded-lg transition-colors duration-300 ${
                      location.pathname === item.path 
                        ? "text-[#0b3a24] bg-[#faf6ee]" 
                        : "text-luxury-charcoal hover:text-[#0b3a24] hover:bg-[#faf6ee]/50"
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>
              
              {/* Auth Button */}
              <div className="p-6 border-t border-gold-100/20">
                {user ? (
                  <div className="space-y-3">
                    <div className="text-center p-3 bg-[#faf6ee] rounded-lg mb-2">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Signed in as</p>
                      <p className="text-xs font-bold text-luxury-charcoal truncate mt-0.5">{user.phoneNumber}</p>
                    </div>
                    <Link
                      to="/profile"
                      className="flex items-center justify-center gap-2 bg-[#0b3a24] text-white px-6 py-2.5 rounded-lg shadow-md hover:bg-[#072a1a] transition-all duration-300 text-xs font-semibold uppercase tracking-wider"
                      onClick={() => setIsOpen(false)}
                    >
                      <User className="h-4 w-4" />
                      <span>Profile</span>
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsOpen(false);
                      }}
                      className="flex items-center justify-center gap-2 w-full border border-red-200 text-red-600 px-6 py-2.5 rounded-lg hover:bg-red-50 transition-all duration-300 text-xs font-semibold uppercase tracking-wider"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                ) : (
                  <Link 
                    to="/login"
                    className="flex items-center justify-center gap-2 bg-[#0b3a24] text-white px-6 py-2.5 rounded-lg shadow-md hover:bg-[#072a1a] transition-all duration-300 text-xs font-semibold uppercase tracking-wider"
                    onClick={() => setIsOpen(false)}
                  >
                    <span>Login</span>
                  </Link>
                )}
              </div>

            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;